import { useSelector } from 'react-redux';
import { ErrorIcon as ErrorIconBase } from '../../Pages/BusinessInfo';
import styled from 'styled-components';

const PaymentDeclinedAlert = () => {
  const policyPurchaseErrors = useSelector((state: ReduxState) => state.policyPurchaseErrors || []);

  if (policyPurchaseErrors.length === 0) return null;

  return (
    <AlertContainer>
      <Title>
        <ErrorIcon /> Payment declined
      </Title>

      {Array.isArray(policyPurchaseErrors) ? (
        policyPurchaseErrors.map((error, index) => <ErrorItem key={index}>{mapDeclineReason(error)}</ErrorItem>)
      ) : (
        <ErrorItem>{mapDeclineReason(policyPurchaseErrors)}</ErrorItem>
      )}
    </AlertContainer>
  );
};

export const TOO_MANY_ATTEMPTS_MESSAGE =
  'Too many attempts or card limit exceeded. If the problem persists, please contact us via chat.';
export const INSUFFICIENT_FUNDS_MESSAGE =
  'Your card has insufficient funds. If the problem persists, please contact us via chat.';
export const INVALID_SECURITY_CODE_MESSAGE =
  "Your card's security code is incorrect. Please verify your card details and try again.";
export const CARD_DECLINED_MESSAGE =
  'Please check that the information you entered is correct and try again. If the problem persists, please contact us via chat.';
export const CARD_NOT_SUPPORTED_MESSAGE =
  'Your card does not support this type of purchase. Please check that the information you entered is correct and try again. If the problem persists, please contact us via chat.';
export const CARD_EXPIRED_MESSAGE = 'Your card has expired. Please use a different card.';
export const PROCESSING_ERROR_MESSAGE =
  'An error occurred while processing your card. Please try again. If the problem persists, please contact us via chat.';

// take the server decline response and map it to a more friendly one
export const mapDeclineReason = (message: string): string => {
  // Handle edge cases
  if (!message || typeof message !== 'string') {
    return message || '';
  }

  const errorMappings: Record<string, string> = {
    'making repeated attempts too frequently': TOO_MANY_ATTEMPTS_MESSAGE,
    'Your card has insufficient funds.': INSUFFICIENT_FUNDS_MESSAGE,
    'invalid security code': INVALID_SECURITY_CODE_MESSAGE,
    'Your card was declined': CARD_DECLINED_MESSAGE,
    'Your card does not support this type of purchase': CARD_NOT_SUPPORTED_MESSAGE,
    'Your card has expired': CARD_EXPIRED_MESSAGE,
    "Your card's security code is incorrect": INVALID_SECURITY_CODE_MESSAGE,
    'An error occurred while processing your card': PROCESSING_ERROR_MESSAGE,
  };

  // Check for partial matches (case insensitive)
  for (const [key, value] of Object.entries(errorMappings)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return value;
    }
  }
  // Return original message if no mapping found
  return message;
};

export const ErrorIcon = styled(ErrorIconBase)`
  margin-right: 12px;
  width: 16px;
  height: 16px;

  g {
    fill: #c80844;
  }
`;

export const AlertContainer = styled.div`
  margin-top: 24px;
  margin-bottom: 24px;
  display: flex;
  padding: 12px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.alert.twenty};
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.08);
  flex-direction: column;
`;

export const IconWrapper = styled.div`
  margin-right: 12px;
  display: flex;
  margin-top: 4px;
  align-items: flex-start;
`;

export const Title = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.tertiary.zero};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  margin-top: 0;
  margin-bottom: 0;
`;

export const ErrorItem = styled.p`
  color: #c80844;
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
  margin-left: 28px;
`;

export default PaymentDeclinedAlert;
